// src/pages/OptimizeSpam.js
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";

// Simple email validation helper function
const isValidEmail = (email) => {
  const valid = /^\S+@\S+\.\S+$/.test(email);
  //console.log("[OptimizeSpam] Validating email:", email, "=>", valid);
  return valid;
};

function OptimizeSpam() {
  const navigate = useNavigate();

  // Retrieve userId from authData in localStorage
  const authData = localStorage.getItem("authData");
  //console.log("[OptimizeSpam] Retrieved authData:", authData);
  const parsedAuthData = authData ? JSON.parse(authData) : null;
  const userId = parsedAuthData?.user?.sub;
  //console.log("[OptimizeSpam] Extracted userId:", userId);

  const [draftId, setDraftId] = useState(null);
  const [optimizedDraft, setOptimizedDraft] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // New states for sending email
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailFormat, setEmailFormat] = useState("html");
  const [draftType, setDraftType] = useState("personalized_spam_optimized"); // default value
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState("");

  useEffect(() => {
    //console.log("[OptimizeSpam] useEffect triggered");
    if (!userId) {
      console.warn("[OptimizeSpam] No userId found. User is not logged in.");
      setError("You are not logged in. Please log in.");
    }

    // Retrieve draftId from localStorage
    const storedDraftId = localStorage.getItem("draftId");
    //console.log("[OptimizeSpam] Retrieved storedDraftId:", storedDraftId);
    if (!storedDraftId || !userId) {
      console.warn("[OptimizeSpam] Missing draftId or userId. Redirecting to /callback");
      navigate("/callback", { replace: true });
    } else {
      setDraftId(storedDraftId);
      //console.log("[OptimizeSpam] Setting draftId state to:", storedDraftId);
    }
  }, [navigate, userId]);

  const handleOptimizeForSpam = async () => {
    //console.log("[OptimizeSpam] handleOptimizeForSpam called");
    if (!draftId || !userId) {
      console.error("[OptimizeSpam] Draft ID or User ID is missing.");
      setError("Draft ID or User ID is missing.");
      return;
    }
    setIsLoading(true);
    setError("");

    // Construct payload with draftId and userId
    const payload = {
      body: JSON.stringify({ draftId, userId }),
    };
    //console.log("[OptimizeSpam] Payload for spam optimization:", payload);

    try {
      const OPTIMIZE_SPAM_API_URL =
        process.env.REACT_APP_OPTIMIZED_URL;
      //console.log("[OptimizeSpam] Using OPTIMIZE_SPAM_API_URL:", OPTIMIZE_SPAM_API_URL);

      const response = await fetch(OPTIMIZE_SPAM_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        // We are sending a JSON-stringified payload that contains a 'body' field
        body: JSON.stringify(payload),
      });
      //console.log("[OptimizeSpam] Received response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        // console.error(
        //   "[OptimizeSpam] Error optimizing draft for spam:",
        //   response.status,
        //   response.statusText,
        //   errorText
        // );
        throw new Error(
          `Error optimizing draft for spam: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data = await response.json();
      //console.log("[OptimizeSpam] Raw data from optimization API:", data);

      // Our lambda returns a JSON string within the 'body' field:
      const parsedData = JSON.parse(data.body);
      //console.log("[OptimizeSpam] Parsed data from optimization API:", parsedData);

      // Get the optimized draft returned by the API
      const { optimizedDraft: newOptimizedDraft } = parsedData;
      if (!newOptimizedDraft) {
        //console.error("[OptimizeSpam] API response is missing optimizedDraft.");
        throw new Error("API response is missing optimizedDraft.");
      }

      // Update state so that the UI displays the optimized draft
      setOptimizedDraft(newOptimizedDraft);
      //console.log("[OptimizeSpam] Updated optimizedDraft state:", newOptimizedDraft);
    } catch (err) {
      //console.error("[OptimizeSpam] Failed to optimize draft for spam. Error:", err);
      setError("Failed to optimize draft for spam. Please try again later.");
    } finally {
      setIsLoading(false);
      //console.log("[OptimizeSpam] Finished handleOptimizeForSpam");
    }
  };

  const handleCopy = () => {
    //console.log("[OptimizeSpam] handleCopy called");
    navigator.clipboard.writeText(optimizedDraft);
    setIsCopied(true);
    //console.log("[OptimizeSpam] Optimized draft copied to clipboard.");
    setTimeout(() => {
      setIsCopied(false);
      //console.log("[OptimizeSpam] isCopied reset to false after timeout");
    }, 2000);
  };

  const handleDownload = () => {
    //console.log("[OptimizeSpam] handleDownload called");
    const blob = new Blob([optimizedDraft], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "optimized_draft.txt";
    link.click();
    //console.log("[OptimizeSpam] Triggered download for optimized draft.");
  };

  /**
   * Send the email to your API Gateway endpoint in the required format:
   *
   * {
   *   "body": "{\"userId\": \"<userId>\", \"campaignId\": \"<campaignId>\", \"draftId\": \"<draftId>\", \"recipientEmail\": \"<recipientEmail>\", \"Email\": \"<Email>\", \"draftType\": \"<draftType>\", \"FirstName\": \"<FirstName>\", \"LastName\": \"<LastName>\", \"Company\": \"<Company>\", \"LinkedIn\": \"<LinkedIn>\", \"recipientName\": \"<recipientName>\"}"
   * }
   */
  const handleSendEmail = async () => {
    //console.log("[OptimizeSpam] handleSendEmail called");
    if (!optimizedDraft) {
      console.warn("[OptimizeSpam] No optimized draft available to send.");
      setSendStatus("There is no optimized draft to send.");
      return;
    }
    if (!recipientEmail) {
      console.warn("[OptimizeSpam] Recipient email is missing.");
      setSendStatus("Please provide a recipient email address.");
      return;
    }
    if (!isValidEmail(recipientEmail)) {
      console.warn("[OptimizeSpam] Provided recipient email is invalid:", recipientEmail);
      setSendStatus("Please provide a valid recipient email address.");
      return;
    }
    setIsSending(true);
    setSendStatus("");

    const SEND_EMAIL_API_URL =
      process.env.REACT_APP_SENDMAIL_URL;
    //console.log("[OptimizeSpam] Using SEND_EMAIL_API_URL:", SEND_EMAIL_API_URL);

    // Construct the payload according to the required format
    const payload = {
      body: JSON.stringify({
        userId: userId,
        campaignId: "12345", // empty if not available
        draftId: draftId,
        recipientEmail: recipientEmail,
        Email: "", // empty if not available
        draftType: draftType,
        FirstName: "",    // empty if not available
        LastName: "",     // empty if not available
        Company: "",      // empty if not available
        LinkedIn: "",     // empty if not available
        recipientName: "" // empty if not available
      })
    };
    //console.log("[OptimizeSpam] Payload for sending email:", payload);

    try {
      const response = await fetch(SEND_EMAIL_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      //console.log("[OptimizeSpam] Received response status from send email API:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        // console.error(
        //   "[OptimizeSpam] Error sending email:",
        //   response.status,
        //   response.statusText,
        //   errorText
        // );
        throw new Error(
          `Error sending email: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const data = await response.json();
      //console.log("[OptimizeSpam] Raw data from send email API:", data);
      const parsedData = JSON.parse(data.body);
      //console.log("[OptimizeSpam] Parsed data from send email API:", parsedData);

      if (
        parsedData.error &&
        parsedData.error.toLowerCase().includes("not verified")
      ) {
        setSendStatus(
          "Sender's identity has not been verified. Please update your sender email in the Settings page."
        );
      } else {
        setSendStatus("Email sent successfully!");
      }
      //console.log("[OptimizeSpam] Email send status:", sendStatus);
    } catch (err) {
      //console.error("[OptimizeSpam] Failed to send email. Error:", err);
      setSendStatus("Failed to send email. Please try again later.");
    } finally {
      setIsSending(false);
      //console.log("[OptimizeSpam] Finished handleSendEmail");
    }
  };

  return (
    <Layout>
      {/* Dashboard Steps Section */}
      <div className="py-2 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-x-4 text-xs py-1 px-3 bg-transparent">
            <div className="flex items-center">
              <span className="inline-flex items-center justify-center w-3 sm:w-4 h-3 sm:h-4 mr-1 text-[8px] sm:text-[10px] font-bold text-gray-500 bg-transparent border border-gray-300 rounded-full">
                1
              </span>
              Generic Draft
            </div>
            <span className="hidden sm:block w-4 h-[1px] bg-gray-300" />
            <div className="flex items-center">
              <span className="inline-flex items-center justify-center w-3 sm:w-4 h-3 sm:h-4 mr-1 text-[8px] sm:text-[10px] font-bold text-gray-700 bg-transparent border border-gray-700 rounded-full">
                2
              </span>
              Personalized
            </div>
            <span className="hidden sm:block w-4 h-[1px] bg-gray-300" />
            <div className="flex items-center">
              <span className="inline-flex items-center justify-center w-3 sm:w-4 h-3 sm:h-4 mr-1 text-[8px] sm:text-[10px] font-bold text-white bg-indigo-600 rounded-full">
                3
              </span>
              Optimized for Email
            </div>
          </nav>
        </div>
      </div>

      {/* Main Page Content */}
      <div className="container mx-auto mt-10 max-w-4xl bg-white shadow-md p-10 rounded-md">
        <h1 className="text-xl font-semibold mb-6 flex items-center">
          Finalize Your Draft
        </h1>
        <p className="text-gray-600 mb-10">
          Ensuring your draft is spam optimized for higher delivery rates.
        </p>

        {/* Error Message */}
        {error && <p className="text-red-500 mb-6">{error}</p>}

        {/* Optimized Draft Output */}
        {optimizedDraft && (
          <div className="relative mb-10 p-4 rounded-lg">
            <textarea
              id="optimizedDraft"
              className="w-full border-none bg-gray-50 p-4 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="10"
              value={optimizedDraft}
              readOnly
            />
            <button
              className="absolute top-2 right-2 bg-blue-500 text-white py-1 px-2 rounded-md text-sm hover:bg-blue-600"
              onClick={handleCopy}
            >
              {isCopied ? "Copied!" : "Copy"}
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center mb-6">
            <div className="spinner-border animate-spin h-5 w-5 border-t-2 border-b-2 border-blue-500 rounded-full"></div>
            <span className="ml-2 text-blue-500">Optimizing...</span>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mb-4">
          <p className="text-sm text-gray-500 italic">
            Disclaimer: PreciseDraft utilizes artificial intelligence to assist
            in drafting documents. While we strive for high accuracy, the tool
            may produce errors. Please ensure you review all drafts thoroughly
            before finalizing.
          </p>
        </div>

        {/* Navigation and Action Buttons */}
        <div className="flex justify-between items-center mt-6">
          <button
            className={`bg-blue-500 text-white py-2 px-6 rounded-md mx-4 ${
              isLoading ? "bg-blue-400 cursor-not-allowed" : "hover:bg-blue-600"
            }`}
            onClick={handleOptimizeForSpam}
            disabled={isLoading || !userId}
          >
            Optimize for Email
          </button>

          <button
            className={`py-2 px-6 rounded-md text-white ${
              optimizedDraft
                ? "bg-green-500 hover:bg-green-600"
                : "bg-gray-300 cursor-not-allowed"
            }`}
            onClick={handleDownload}
            disabled={!optimizedDraft || isLoading || !userId}
          >
            Download
          </button>
        </div>

        {/* Send Email Section (display only when optimizedDraft is visible) */}
        {optimizedDraft && (
          <div className="mt-8 border-t pt-8">
            <h2 className="text-xl font-semibold mb-6">Email This Personalized Draft</h2>
            {/* Recipient Email Input */}
            <div className="mb-4">
              <label
                htmlFor="recipientEmail"
                className="block text-sm font-medium text-gray-700"
              >
                Recipient Email
              </label>
              <input
                type="email"
                id="recipientEmail"
                placeholder="recipient@example.com"
                value={recipientEmail}
                onChange={(e) => {
                  setRecipientEmail(e.target.value);
                  //console.log("[OptimizeSpam] Recipient email updated:", e.target.value);
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2"
              />
              {recipientEmail && !isValidEmail(recipientEmail) && (
                <p className="text-xs text-red-500 mt-1">
                  Please enter a valid email address.
                </p>
              )}
            </div>
            {/* Email Format and Draft Type Selects */}
            <div className="mb-4">
              <label
                htmlFor="emailFormat"
                className="block text-sm font-medium text-gray-700"
              >
                Email Format
              </label>
              <select
                id="emailFormat"
                value={emailFormat}
                onChange={(e) => {
                  setEmailFormat(e.target.value);
                  //console.log("[OptimizeSpam] Email format updated:", e.target.value);
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2"
              >
                <option value="text">Text</option>
                <option value="html">HTML</option>
              </select>
            </div>
            <div className="mb-4">
              <label
                htmlFor="draftType"
                className="block text-sm font-medium text-gray-700"
              >
                Draft Type
              </label>
              <select
                id="draftType"
                value={draftType}
                onChange={(e) => {
                  setDraftType(e.target.value);
                  //console.log("[OptimizeSpam] Draft type updated:", e.target.value);
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2"
              >
                <option value="personalizedDraft">Personalized Only</option>
                <option value="optimizedDraft">
                  Personalized and Spam Optimized
                </option>
              </select>
            </div>
            {/* Small print message about sender's email */}
            <p className="text-xs text-gray-500 mb-2">
              Note: The sender's email should be specified in the{" "}
              <Link to="/settings" className="underline">
                Settings
              </Link>{" "}
              page.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                className={`py-2 px-6 rounded-md text-white ${
                  isSending || !recipientEmail || !isValidEmail(recipientEmail)
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-purple-500 hover:bg-purple-600"
                }`}
                onClick={handleSendEmail}
                disabled={
                  isSending ||
                  !optimizedDraft ||
                  !recipientEmail ||
                  !isValidEmail(recipientEmail)
                }
              >
                {isSending ? "Sending..." : "Send Email"}
              </button>
            </div>
            {sendStatus && (
              <p
                className={`mt-4 text-sm ${
                  sendStatus.toLowerCase().includes("failed") ||
                  sendStatus.toLowerCase().includes("not verified")
                    ? "text-red-500"
                    : "text-green-500"
                }`}
              >
                {sendStatus.toLowerCase().includes("not verified") ? (
                  <>
                    {sendStatus}{" "}
                    <Link to="/settings" className="underline">
                      Settings page
                    </Link>
                  </>
                ) : (
                  sendStatus
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default OptimizeSpam;
